import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { clearSessionTokens, clearStoredAccounts } from '../lib/session';
import { t } from '../lib/i18n';
import { api } from '../lib/trpc/client';

export function SettingsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [me, setMe] = useState<{ userId: string; scratchUsername: string; createdAt: string } | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const [activeCircleName, setActiveCircleName] = useState('');

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const [profile, activeCircle] = await Promise.all([
          api.auth.me(),
          api.circle.getActiveCircle()
        ]);
        setMe(profile);
        setActiveCircleName(activeCircle?.name ?? '');
      } catch (err) {
        setError(err instanceof Error ? err.message : t('アカウント情報の取得に失敗しました', 'Failed to load account info'));
      } finally {
        setLoading(false);
      }
    };

    void run();
  }, []);

  const handleLogout = async () => {
    setLoggingOut(true);
    setError(null);
    try {
      await api.auth.logout();
      clearSessionTokens();
      clearStoredAccounts();
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('ログアウトに失敗しました', 'Failed to logout'));
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <main className="mx-auto min-h-screen w-full max-w-2xl space-y-4 px-4 py-10">
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold">{t('設定', 'Settings')}</h1>
        <p className="text-sm text-muted-foreground">{t('セッションとサークルの管理を行います。', 'Manage your session and circles.')}</p>
      </header>

      {error ? (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{t('アカウント情報', 'Account Info')}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          ) : (
            <div className="space-y-1 text-sm">
              <p>{t('ユーザーID', 'User ID')}: {me?.userId ?? '-'}</p>
              <p>{t('ユーザー名', 'Username')}: {me?.scratchUsername ?? '-'}</p>
              <p>{t('作成日時', 'Created')}: {me ? new Date(me.createdAt).toLocaleString() : '-'}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('サークル', 'Circle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Label>{t('現在のサークル', 'Current circle')}</Label>
          <p className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
            {activeCircleName || t('未選択', 'Not selected')}
          </p>
          <Button variant="outline" onClick={() => navigate('/circles')}>
            {t('サークル管理へ', 'Manage circles')}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('ログアウト', 'Logout')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Button variant="destructive" onClick={handleLogout} disabled={loggingOut}>
            {loggingOut ? t('ログアウト中...', 'Logging out...') : t('ログアウト', 'Logout')}
          </Button>
        </CardContent>
      </Card>
    </main>
  );
}
